import { useEffect, useState } from 'preact/hooks'

export interface FeedState<T> {
  data: T | null
  error: string | null
  loading: boolean
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

/**
 * Load a feed, keep it fresh, and never outlive the element.
 *
 * Two properties matter for an embedded widget. The AbortController means an
 * attribute change (or the element leaving the DOM) cancels the request in
 * flight, so a slow earlier reply cannot land last and overwrite fresher data.
 * And a failed *refresh* keeps the data already on screen — a stale score beats
 * an error message replacing a table the reader was looking at.
 */
export function useFeed<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
  refreshSeconds = 0,
): FeedState<T> {
  const [state, setState] = useState<FeedState<T>>({
    data: null,
    error: null,
    loading: true,
  })

  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false

    const run = async (isInitial: boolean): Promise<void> => {
      if (isInitial) setState({ data: null, error: null, loading: true })
      try {
        const data = await load(controller.signal)
        if (!cancelled) setState({ data, error: null, loading: false })
      } catch (error) {
        if (cancelled || controller.signal.aborted) return
        setState((previous) => ({
          data: previous.data,
          error: messageOf(error),
          loading: false,
        }))
      }
    }

    void run(true)

    const timer =
      refreshSeconds > 0
        ? setInterval(() => void run(false), refreshSeconds * 1000)
        : undefined

    return () => {
      cancelled = true
      controller.abort()
      if (timer !== undefined) clearInterval(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, refreshSeconds])

  return state
}
